import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Check, Copy, Dices, Gem, Info, Joystick, KeyRound, Link2,
  Loader2, Save, Spade, X,
} from 'lucide-react';
import client from '../lib/api';
import SecretInput from './components/SecretInput';
import { useToast } from '../hooks/use-toast';
import { notifySettingsUpdated } from '../context/SettingsContext';
import { GAME_LANGUAGES, GAME_LANGUAGE_HINT } from '../constants/gameLanguages';

const MASKED_SECRET = '••••••••••••••••';

const SERVICES = {
  goldpalace: {
    id: 'goldpalace',
    name: 'Gold Palace',
    icon: Spade,
    accent: '#22c55e',
    enabledKey: 'goldpalace_enabled',
    note: 'Callback Token boş bırakılırsa kaydetme sırasında güvenli biçimde otomatik oluşturulur; tokenlar tekrar gösterilmez.',
    callbackPath: '/api/goldpalace_api',
    scopes: [
      { key: 'slot_goldpalace_enabled', label: 'Slot oyunları' },
      { key: 'live_casino_goldpalace_enabled', label: 'Canlı casino' },
    ],
    fields: [
      {
        key: 'goldpalace_api_url',
        label: 'API URL',
        placeholder: 'https://agent.goldslotpalase.com',
        defaultValue: 'https://agent.goldslotpalase.com',
        required: true,
        full: true,
      },
      { key: 'goldpalace_api_token', label: 'API Token', secret: true, required: true },
      {
        key: 'goldpalace_callback_token',
        label: 'Callback Token',
        secret: true,
        required: true,
        autoGenerate: true,
      },
      {
        key: 'goldpalace_return_url',
        label: 'Oyundan Dönüş URL’si',
        placeholder: '/slotlar',
        defaultPath: '/slotlar',
        full: true,
      },
    ],
  },
  oroplay: {
    id: 'oroplay',
    name: 'OroPlay',
    icon: Gem,
    accent: '#f472b6',
    enabledKey: 'oroplay_enabled',
    note: 'OroPlay seamless cüzdanla çalışır: clientId/clientSecret hem giden isteklerin bearer token’ını üretir hem de sağlayıcının bize gönderdiği webhook’un Basic auth’unu doğrular. Sağlayıcı IP beyaz listesi uygular; sunucunun çıkış IP’si OroPlay panelinde ekli değilse bağlantı 403 döner.',
    // Sağlayıcı kendi ek yolunu koyabildiği için uç iki biçimde de
    // karşılanır; panele taban adres yazılır.
    callbackPath: '/api/oroplay',
    scopes: [
      { key: 'slot_oroplay_enabled', label: 'Slot oyunları' },
      { key: 'live_casino_oroplay_enabled', label: 'Canlı casino' },
    ],
    fields: [
      {
        key: 'oroplay_api_url',
        label: 'API URL',
        placeholder: 'https://oroplay.com/api/v2',
        defaultValue: 'https://oroplay.com/api/v2',
        required: true,
        full: true,
      },
      { key: 'oroplay_client_id', label: 'Client ID', required: true },
      {
        key: 'oroplay_client_secret',
        label: 'Client Secret',
        secret: true,
        required: true,
      },
      {
        key: 'oroplay_return_url',
        label: 'Oyundan Dönüş URL’si (lobbyUrl)',
        placeholder: '/slotlar',
        defaultPath: '/slotlar',
        full: true,
      },
    ],
  },
  drakon: {
    id: 'drakon',
    name: 'Drakon',
    icon: Dices,
    accent: '#38bdf8',
    enabledKey: 'drakon_enabled',
    note: 'Drakon oyunu açmadan önce callback adresine bakiye sorar. Kimliğini isteğin gövdesinde agent bilgileriyle bildirir; ayrıca bir başlık göndermez. İzinli IP listesi isteğe bağlıdır — yalnızca kimlik bilgisi taşımayan çağrılar için yedek kapıdır.',
    // Drakon adresin sonuna /drakon_api ekini kendisi ekler; panele ek olmadan
    // yazılmalıdır, yoksa istek .../drakon_api/drakon_api adresine gidip 404 olur.
    callbackPath: '/api',
    scopes: [
      { key: 'slot_drakon_enabled', label: 'Slot oyunları' },
      { key: 'live_casino_drakon_enabled', label: 'Canlı casino' },
    ],
    fields: [
      {
        key: 'drakon_api_url',
        label: 'API URL',
        placeholder: 'https://gator.drakonapi.tech/api/v1',
        defaultValue: 'https://gator.drakonapi.tech/api/v1',
        required: true,
        full: true,
      },
      {
        key: 'drakon_agent_code',
        label: 'Agent Code',
        placeholder: 'Agent code',
        // Sağlayıcı panelinde zorunlu alan; boş giderse launch çağrısı
        // tanınmaz ve oyun açılmaz.
        required: true,
      },
      { key: 'drakon_agent_token', label: 'Agent Token', secret: true, required: true },
      {
        key: 'drakon_agent_secret',
        label: 'Agent Secret',
        secret: true,
        required: true,
        full: true,
      },
      {
        key: 'drakon_webhook_secret',
        label: 'Webhook Secret (Drakon göndermez — yalnızca ara katman kullanıyorsanız)',
        secret: true,
        full: true,
      },
      {
        key: 'drakon_allowed_ips',
        label: 'İzinli webhook IP adresleri (isteğe bağlı)',
        placeholder: '203.0.113.10, 203.0.113.11',
        full: true,
        // Zorunlu değildir: Drakon bahis/kazanç çağrılarında da agent
        // bilgilerini gövdede gönderir ve çağrı onlarla doğrulanır. Liste
        // yalnızca kimlik alanı taşımayan bir çağrı gelirse devreye girer;
        // zorunlu tutulduğunda servis açılamıyor, katalog çekilemiyordu.
      },
    ],
  },
  scorpio: {
    id: 'scorpio',
    name: 'ScorpioPlay',
    icon: Joystick,
    accent: '#a78bfa',
    enabledKey: 'scorpio_enabled',
    note: 'Transfer/seamless modu sağlayıcıdaki Callback URL durumuna göre otomatik belirlenir.',
    callbackPath: '/api/scorpio_api',
    scopes: [
      { key: 'live_casino_scorpio_enabled', label: 'Canlı casino' },
    ],
    fields: [
      {
        key: 'scorpio_api_url',
        label: 'API URL',
        placeholder: 'https://api.scorpioplay.com',
        required: true,
        full: true,
      },
      { key: 'scorpio_api_token', label: 'API Token', secret: true, required: true },
      { key: 'scorpio_operator_id', label: 'Operatör ID', placeholder: 'Operatör kodu' },
      {
        key: 'scorpio_return_url',
        label: 'Oyundan Dönüş URL’si',
        placeholder: '/slotlar',
        defaultPath: '/slotlar',
        full: true,
      },
    ],
  },
};

const COMMON_KEYS = [
  'game_api_currency',
  'game_api_language',
  'game_api_rtp',
  'demo_enabled',
  'demo_requires_login',
  'demo_start_balance',
  'demo_rtp',
];

// Adres tabanları sunucudan gelir; tarayıcının origin'i kullanılamaz. Panel
// ayrı bir alan adına taşındığında callback ve dönüş adresleri panelin
// adresine yazılırdı (bkz. backend/services/site_address.py address_bases).
const baseFor = (data, role) => (
  data?.address_bases?.[role] || data?.address_bases?.site || ''
);

const hydrateSettings = (data, service) => {
  const next = { ...data };
  service.fields.filter((field) => field.defaultValue).forEach((field) => {
    if (!String(next[field.key] || '').trim()) next[field.key] = field.defaultValue;
  });
  // Oyundan dönüş adresi oyuncunun tarayıcısına verilir: yaşayan ana siteden
  // üretilir. Site adresi bilinmiyorsa alan boş bırakılır — yanlış adres
  // yazmak oyuncuyu yayında olmayan bir siteye atardı.
  const siteBase = data?.address_bases?.site || '';
  if (siteBase) {
    service.fields.filter((field) => field.defaultPath).forEach((field) => {
      if (!String(next[field.key] || '').trim()) {
        next[field.key] = `${siteBase}${field.defaultPath}`;
      }
    });
  }
  service.fields.filter((field) => field.secret).forEach((field) => {
    next[field.key] = data[`${field.key}_configured`] ? MASKED_SECRET : '';
  });
  return next;
};

const Field = ({ label, full = false, hint, children }) => (
  <label className={`gc-field ${full ? 'is-full' : ''}`}>
    <span className="gc-label">{label}</span>
    {children}
    {hint && <span className="gc-hint">{hint}</span>}
  </label>
);

const GameApiSettingsPanel = ({ serviceId, onClose, onSaved }) => {
  const service = SERVICES[serviceId] || SERVICES.drakon;
  const ServiceIcon = service.icon;
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    client.get('/admin/settings')
      .then(({ data }) => {
        if (active) setSettings(hydrateSettings(data, service));
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError.response?.data?.detail || 'Servis ayarları alınamadı');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [serviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Eksik zorunlu alanlar formun başında adlarıyla sayılır: eskiden yalnızca
  // "Bilgi eksik" rozeti vardı ve hangi alanın eksik olduğunu bulmak için
  // bütün formu taramak gerekiyordu.
  const missingFields = useMemo(() => {
    if (!settings) return [];
    return service.fields
      .filter((field) => field.required && !field.autoGenerate)
      .filter((field) => (
        !String(settings[field.key] || '').trim()
        && !settings[`${field.key}_configured`]
      ))
      .map((field) => field.shortLabel || field.label);
  }, [service, settings]);

  const requiredReady = missingFields.length === 0;

  const update = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  // Sağlayıcı panelinde tanımlanacak adres: oyun bildirimleri rolüne atanmış
  // adresten, rol atanmamışsa yaşayan ana siteden üretilir.
  const callbackBase = baseFor(settings, 'game');
  const callbackUrl = callbackBase
    ? `${callbackBase}${service.callbackPath}` : service.callbackPath;

  const copyCallback = async () => {
    try {
      await navigator.clipboard.writeText(callbackUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Adresi elle kopyalayın.' });
    }
  };

  const save = async () => {
    const payload = {};
    [
      ...service.scopes.map((scope) => scope.key),
      ...service.fields.map((field) => field.key),
      ...COMMON_KEYS,
    ].forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(settings, key)) {
        payload[key] = settings[key];
      }
    });

    service.fields.filter((field) => field.secret).forEach((field) => {
      if (!payload[field.key] || payload[field.key] === MASKED_SECRET) {
        delete payload[field.key];
      }
    });

    const callbackWillBeGenerated = (
      service.id === 'goldpalace'
      && !settings.goldpalace_callback_token_configured
      && !payload.goldpalace_callback_token
    );

    setSaving(true);
    try {
      const { data } = await client.patch('/admin/settings', payload);
      setSettings(hydrateSettings(data, service));
      notifySettingsUpdated();
      toast({
        title: `${service.name} ayarları kaydedildi`,
        description: callbackWillBeGenerated && data.goldpalace_callback_token_configured
          ? 'Callback Token otomatik oluşturuldu.'
          : undefined,
      });
      await onSaved?.();
    } catch (requestError) {
      toast({
        title: 'Ayarlar kaydedilemedi',
        description: requestError.response?.data?.detail || 'İşlem başarısız',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="gc-state is-inline">
        <Loader2 size={20} className="gc-spin" />
        <p>{service.name} ayarları yükleniyor…</p>
      </div>
    );
  }

  if (error || !settings) {
    return (
      <div className="gc-state">
        <X size={24} color="#fb7185" />
        <h2>Ayarlar yüklenemedi</h2>
        <p>{error || 'Servis ayarları alınamadı.'}</p>
        <button type="button" onClick={onClose} className="gc-btn">Kapat</button>
      </div>
    );
  }

  return (
    <section className="gc-panel is-tone" style={{ '--gc-tone': service.accent }}>
      <div className="gc-panel-head">
        <span className="gc-card-icon"><ServiceIcon size={19} /></span>
        <div className="gc-panel-title">
          <h2>{service.name} Ayarları</h2>
          <p>{service.note}</p>
        </div>
        <div className="gc-badges">
          <span className={`gc-badge ${requiredReady ? 'is-on' : 'is-warn'}`}>
            {requiredReady ? 'Bilgiler tam' : 'Bilgi eksik'}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="gc-btn is-icon is-sm"
            aria-label="Ayarları kapat"
          >
            <X size={15} />
          </button>
        </div>
      </div>

      <div className="gc-panel-body">
        {missingFields.length > 0 && (
          <p className="gc-note is-warn">
            <AlertTriangle size={14} />
            Eksik zorunlu alan: {missingFields.join(', ')}. Tamamlanmadan servis
            açılamaz ve katalog çekilemez.
          </p>
        )}

        <div className="gc-box">
          <div className="gc-box-head">
            <span className="gc-box-icon"><KeyRound size={15} /></span>
            <div>
              <h3>Bağlantı bilgileri</h3>
              <p>Sağlayıcının panelinden alınan adres ve anahtarlar.</p>
            </div>
          </div>
          <div className="gc-fields">
          {service.fields.map((field) => {
            const saved = !!settings[`${field.key}_configured`];
            const missing = (
              field.required
              && !field.autoGenerate
              && !String(settings[field.key] || '').trim()
              && !saved
            );
            return (
              <label
                key={field.key}
                className={`gc-field ${field.full ? 'is-full' : ''}`}
              >
                <span className="gc-label">
                  {field.label}
                  {saved && <span className="gc-label-tag is-ok">kayıtlı</span>}
                  {!saved && field.autoGenerate && (
                    <span className="gc-label-tag is-info">otomatik oluşturulur</span>
                  )}
                  {missing && <span className="gc-label-tag is-warn">zorunlu</span>}
                </span>
                {field.secret ? (
                  <SecretInput
                    value={settings[field.key] || ''}
                    onChange={(event) => update(field.key, event.target.value)}
                    onFocus={() => {
                      if (settings[field.key] === MASKED_SECRET) update(field.key, '');
                    }}
                    placeholder={
                      saved
                        ? 'Değiştirmek için yeni değer girin'
                        : field.autoGenerate
                          ? 'Boş bırakın; kaydederken oluşturulsun'
                          : field.label
                    }
                  />
                ) : (
                  <input
                    type="text"
                    value={settings[field.key] || ''}
                    onChange={(event) => update(field.key, event.target.value)}
                    className="gc-input"
                    autoComplete="off"
                    placeholder={field.placeholder || ''}
                  />
                )}
              </label>
            );
          })}
          </div>
        </div>

        {service.scopes.length > 0 && (
          <div className="gc-box">
            <div className="gc-box-head">
              <div>
                <h3>Servisin yayınlanacağı bölümler</h3>
                <p>İşareti kaldırılan bölümde bu sağlayıcının oyunları görünmez.</p>
              </div>
            </div>
            <div className="gc-checks">
              {service.scopes.map((scope) => (
                <label key={scope.key} className="gc-check is-plain">
                  <input
                    type="checkbox"
                    checked={settings[scope.key] !== false}
                    onChange={(event) => update(scope.key, event.target.checked)}
                  />
                  <span className="gc-check-text">{scope.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="gc-box is-tone">
          <div className="gc-box-head">
            <span className="gc-box-icon"><Link2 size={15} /></span>
            <div>
              <h3>Sağlayıcı paneline girilecek callback adresi</h3>
              <p>Bu adres sağlayıcının panelinde tanımlanır; istekle gönderilmez.</p>
            </div>
          </div>
          <div className="gc-copy">
            <code className="gc-copy-url">{callbackUrl}</code>
            <button type="button" onClick={copyCallback} className="gc-btn is-sm">
              {copied ? <><Check size={12} /> Kopyalandı</> : <><Copy size={12} /> Kopyala</>}
            </button>
          </div>
        </div>

        <details className="gc-box gc-details">
          <summary>
            Ortak oyun ve demo ayarları
            <span>Para birimi, dil, RTP ve demo cüzdanı — tüm sağlayıcılar için geçerlidir</span>
          </summary>
          <div className="gc-fields">
            <Field label="Para Birimi">
              <input
                value={settings.game_api_currency || 'TRY'}
                onChange={(event) => update('game_api_currency', event.target.value.toUpperCase())}
                className="gc-input"
                placeholder="TRY"
              />
            </Field>
            <Field label="Oyun Dili" hint={GAME_LANGUAGE_HINT}>
              <select
                value={settings.game_api_language || 'tr'}
                onChange={(event) => update('game_api_language', event.target.value)}
                className="gc-input"
              >
                {GAME_LANGUAGES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </Field>
            <Field label="Gerçek Para RTP" full>
              <input
                type="number"
                min="0"
                max="99"
                value={settings.game_api_rtp ?? 90}
                onChange={(event) => update('game_api_rtp', Number(event.target.value))}
                className="gc-input"
              />
            </Field>
            <div className="gc-field is-full">
              <div className="gc-checks">
                <label className="gc-check is-plain">
                  <input
                    type="checkbox"
                    checked={!!settings.demo_enabled}
                    onChange={(event) => update('demo_enabled', event.target.checked)}
                  />
                  <span className="gc-check-text">Demo modu açık</span>
                </label>
                <label className="gc-check is-plain">
                  <input
                    type="checkbox"
                    checked={!!settings.demo_requires_login}
                    onChange={(event) => update('demo_requires_login', event.target.checked)}
                  />
                  <span className="gc-check-text">Demo için giriş zorunlu</span>
                </label>
              </div>
            </div>
            <Field label="Demo Başlangıç Bakiyesi">
              <input
                type="number"
                min="0"
                step="100"
                value={settings.demo_start_balance ?? 10000}
                onChange={(event) => update('demo_start_balance', Number(event.target.value))}
                className="gc-input"
              />
            </Field>
            <Field label="Demo RTP">
              <input
                type="number"
                min="1"
                max="99"
                value={settings.demo_rtp ?? 97}
                onChange={(event) => update('demo_rtp', Number(event.target.value))}
                className="gc-input"
              />
            </Field>
          </div>
        </details>

        <p className="gc-note is-info">
          <Info size={14} />
          Servisi açıp kapatmak için karttaki canlı casino düğmesini kullanın. Bu
          form yalnızca bağlantı ve oyun seçeneklerini kaydeder.
        </p>
      </div>

      <div className="gc-panel-foot">
        <span className="gc-foot-note">
          Sırlar kaydedildikten sonra bir daha gösterilmez; değiştirmek için yeni
          değer girin.
        </span>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="gc-btn is-primary"
        >
          {saving ? <Loader2 size={14} className="gc-spin" /> : <Save size={14} />}
          Ayarları Kaydet
        </button>
      </div>
    </section>
  );
};

export default GameApiSettingsPanel;
